import { Menu, ActionIcon, rem, CopyButton, Anchor } from "@mantine/core"
import {
  IconCopy,
  IconDotsVertical,
  IconDownload,
  IconEdit,
} from "@tabler/icons-react"
import { WebCalendarProfileDto } from "@/orval/gen"
import { useTranslation } from "react-i18next"
import { useIsSmallScreen } from "../../../../../utils/useIsSmallScreen"

interface Props {
  profile: WebCalendarProfileDto
  onEditProfileClick: (profile: WebCalendarProfileDto) => void
}

export function CopyCalendarProfileLinkButton({
  profile,
  component: component,
}: {
  profile: WebCalendarProfileDto
  component: "Button" | "MenuItem"
}) {
  return (
    <CopyButton value={profile.externalLink ?? ""}>
      {({ copied, copy }) =>
        component == "MenuItem" ? (
          <Menu.Item
            onClick={copy}
            leftSection={<IconCopy size={14} />}
            color={copied ? "teal" : "blue"}
            variant="subtle"
          >
            <ProfileLinkAnchor
              copied={copied}
              copy={copy}
              link={profile.externalLink ?? ""}
            />
          </Menu.Item>
        ) : (
          <ProfileLinkAnchor
            copied={copied}
            copy={copy}
            link={profile.externalLink ?? ""}
          />
        )
      }
    </CopyButton>
  )
}

function ProfileLinkAnchor({
  copied,
  link,
  copy,
}: {
  copied: boolean
  link: string
  copy: () => void
}) {
  const { t } = useTranslation("app_server")
  return (
    <Anchor
      c={copied ? "teal" : "blue"}
      size="xs"
      onClick={(e) => {
        e.preventDefault()
        copy()
      }}
      href={link}
    >
      {copied ? t("CopiedLink") : t("CopyLink")}
    </Anchor>
  )
}

export function MoreMenu({ profile, onEditProfileClick }: Props) {
  const { t } = useTranslation("app_server")
  const isMobile = useIsSmallScreen()

  return (
    <Menu withinPortal position="bottom-end" shadow="sm">
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size={isMobile ? 44 : undefined}
          aria-label={t("Actions")}
        >
          <IconDotsVertical style={{ width: rem(16), height: rem(16) }} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        {isMobile && profile.isSubsribed ? (
          <CopyCalendarProfileLinkButton
            profile={profile}
            component="MenuItem"
          />
        ) : null}

        {profile.isSubsribed && (
          <Menu.Item
            onClick={() => {
              window.location.href = profile.internalLink ?? ""
            }}
            leftSection={
              <IconDownload
                style={{
                  width: rem(14),
                  height: rem(14),
                }}
              />
            }
          >
            {t("Download")}
          </Menu.Item>
        )}
        {profile.allowEditAndDelete && (
          <>
            <Menu.Item
              leftSection={
                <IconEdit
                  style={{
                    width: rem(14),
                    height: rem(14),
                  }}
                />
              }
              onClick={() => onEditProfileClick(profile)}
            >
              {t("Edit")}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}
